// ==========================================================================
// API client
//
// One place that knows how to talk to a General ERP backend: it attaches the
// token, refreshes it once when it expires, retries the original request and
// normalises every error into an ApiError carrying the backend's stable `code`.
//
// Built on fetch. No axios (see APPROVED_LIBRARIES.md).
// ==========================================================================

import { createMediaTokens } from './mediaToken.js'

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3050/api/v1'
const TIMEOUT_MS = 30_000

/**
 * Dónde vive esta instancia, para una pantalla que tiene que COMPONER una
 * dirección en vez de llamarla.
 *
 * `window.location.origin` no sirve para eso: es el de la SPA, y hay cosas que
 * sirve el backend y no ella — un artefacto de `/embed`, un endpoint de
 * `/public`. Detrás de un solo proxy coinciden y no se nota; en desarrollo son
 * dos puertos, y lo que se compone apunta a donde no hay nada.
 *
 * Sale del MISMO sitio del que sale la base del API, así que no hay una segunda
 * idea de dónde está el servidor. Una base relativa —`/api/v1`, que es lo que
 * se configura cuando comparten host— resuelve al origen de la página, que en
 * ese caso es exactamente el correcto.
 */
export function instanceOrigin() {
  try {
    return new URL(BASE_URL, window.location.origin).origin
  } catch {
    return window.location.origin
  }
}

/**
 * La raíz del servidor CON su subruta: el origen más lo que va delante de
 * `/api/v1`. Es donde viven `/health` y las rutas que no son del API.
 *
 * El origen solo no basta cuando la instancia cuelga de una subruta: la demo de
 * obra vive en `/construccion/`, y `${instanceOrigin()}/health` preguntaba a la
 * RAÍZ del dominio —otra instancia, o nadie: un 502—, así que la ayuda y el menú
 * condicionado no sabían qué módulos había.
 */
export function instanceRoot() {
  try {
    const url = new URL(BASE_URL, window.location.origin)
    return `${url.origin}${url.pathname.replace(/\/api\/v1\/?$/, '').replace(/\/$/, '')}`
  } catch {
    return window.location.origin
  }
}

export const STORAGE = {
  access: 'general.accessToken',
  refresh: 'general.refreshToken',
  user: 'general.user',
}

/**
 * Carries the backend's error contract intact (§16):
 *   code      stable, from the catalogue — what the UI translates
 *   details   values the translation interpolates
 *   fields    per-input problems, so a form can highlight them
 *   requestId what support pastes to find the log line
 */
export class ApiError extends Error {
  constructor(message, status, code, { details, fields, requestId } = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details ?? null
    this.fields = fields ?? null
    this.requestId = requestId ?? null
  }
}

// Endpoints where a 401 is a legitimate answer, not an expired session. Trying
// to refresh on these would loop.
const PUBLIC_PATHS = [
  '/auth/login', '/auth/refresh', '/auth/mfa/validate',
  '/auth/password/forgot', '/auth/password/reset',
]

// ── What every screen should hear about, whoever made the call ───────────────
//
// A demo refuses every write (DEMO_READ_ONLY). Each form would otherwise show
// its own error, and most show a generic one; the demo package listens for this
// and explains, once and kindly, that nothing is saved here. On an instance
// that is not a demo the code never arrives and nothing listens.
export const DEMO_READ_ONLY_EVENT = 'general:demo-read-only'

function announce(code) {
  if (code === 'DEMO_READ_ONLY') window.dispatchEvent(new CustomEvent(DEMO_READ_ONLY_EVENT))
}

/**
 * The session is gone. `detail.code` says why when the server said so — the
 * shell treats them all alike, a demo sends DEMO_SESSION_ENDED to its front page.
 */
function sessionLost(error) {
  window.dispatchEvent(new CustomEvent('general:unauthorized', { detail: { code: error?.code ?? null } }))
}

// ── Single-flight refresh ─────────────────────────────────────────────────
// Ten requests failing at once must trigger ONE refresh, not ten. The rest wait
// on the same promise.
let refreshing = null

// El token de las URL de ficheros (mediaToken.js): se pide por detrás con la
// cabecera de siempre y urlWithToken lo usa en cuanto lo tiene.
const mediaTokens = createMediaTokens({ fetchToken: () => request('POST', '/auth/media-token') })

export function getToken() { return localStorage.getItem(STORAGE.access) }

export function setTokens({ accessToken, refreshToken }) {
  if (accessToken) localStorage.setItem(STORAGE.access, accessToken)
  if (refreshToken) localStorage.setItem(STORAGE.refresh, refreshToken)
}

export function clearSession() {
  mediaTokens.clear()
  localStorage.removeItem(STORAGE.access)
  localStorage.removeItem(STORAGE.refresh)
  localStorage.removeItem(STORAGE.user)
}

async function refreshTokens() {
  const refreshToken = localStorage.getItem(STORAGE.refresh)
  if (!refreshToken) throw new ApiError('No refresh token', 401, 'SESSION_EXPIRED')

  const response = await fetch(`${BASE_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  })

  if (!response.ok) {
    // The reason travels with the failure: a demo visit that reached its end
    // (DEMO_SESSION_ENDED) is sent back to the front page, not to a sign-in.
    let reason = null
    try { reason = (await response.json())?.code ?? null } catch { /* empty body */ }
    throw new ApiError('Session expired', 401, reason ?? 'SESSION_EXPIRED')
  }

  const { data } = await response.json()
  setTokens(data)
  // The shell listens for this to reconnect the notification stream, which
  // carries the token in its URL and therefore dies with the old one.
  window.dispatchEvent(new CustomEvent('general:token-refreshed', { detail: data }))
  return data.accessToken
}

function ensureFreshToken() {
  if (!refreshing) {
    refreshing = refreshTokens().finally(() => { refreshing = null })
  }
  return refreshing
}

/**
 * A query string from an object, or '' when there is nothing to say.
 *
 * Empty, null and undefined are dropped rather than sent. A cleared filter is
 * bound to a `ref('')`, and `?status=` is not "no filter" to a validator that
 * checks the value is one of a list — it is an invalid one, which comes back as
 * a 400 on a screen where the person did nothing but clear a box.
 */
function queryString(params) {
  if (!params) return ''
  const query = new URLSearchParams(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
  ).toString()
  return query ? `?${query}` : ''
}

async function request(method, path, body, options = {}, isRetry = false) {
  const token = getToken()
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), options.timeout ?? TIMEOUT_MS)

  let response
  try {
    response = await fetch(`${BASE_URL}${path}${queryString(options.params)}`, {
      method,
      headers: {
        ...(body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
      body: body instanceof FormData ? body : (body !== undefined ? JSON.stringify(body) : undefined),
      signal: controller.signal,
      // Lo que se manda al cerrar la pestaña (colgar una llamada): el navegador
      // lo termina aunque la página ya no exista.
      keepalive: Boolean(options.keepalive),
    })
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new ApiError('The request took too long. Try again.', 0, 'TIMEOUT')
    }
    throw new ApiError('Network error', 0, 'NETWORK_ERROR')
  } finally {
    clearTimeout(timeout)
  }

  if (response.status === 204) return null

  let payload = null
  try { payload = await response.json() } catch { /* empty body */ }

  // ── 401 ───────────────────────────────────────────────────────────────────
  // Two very different things arrive as a 401, and only one of them is worth
  // retrying. An expired access token is routine: refresh once and replay.
  // SESSION_REVOKED means the session itself is gone — signed out from another
  // device, closed by an administrator, the account deactivated — and refreshing
  // would not only fail, it would look like a stolen token being replayed and
  // land in the session history as one. Sign out and say so.
  if (response.status === 401 && !isRetry && !PUBLIC_PATHS.includes(path)) {
    if (payload?.code === 'SESSION_REVOKED') {
      clearSession()
      sessionLost({ code: 'SESSION_REVOKED' })
      throw new ApiError(payload.message ?? 'This session was closed', 401, 'SESSION_REVOKED')
    }

    try {
      await ensureFreshToken()
      return request(method, path, body, options, true)
    } catch (error) {
      clearSession()
      // The shell shows the message and redirects; the client stays dumb.
      sessionLost(error)
      throw new ApiError('Session expired', 401, 'SESSION_EXPIRED')
    }
  }

  if (!response.ok) {
    announce(payload?.code)
    throw new ApiError(
      payload?.message || `Error ${response.status}`,
      response.status,
      payload?.code || 'INTERNAL_ERROR',
      {
        details: payload?.details,
        fields: payload?.fields,
        // Also read from the header, so even a response with no body (a proxy
        // timing out, say) still gives support something to trace.
        requestId: payload?.requestId ?? response.headers.get('X-Request-Id'),
      }
    )
  }

  // The backend always answers { success, data } - callers want the data.
  return payload?.data ?? payload
}

/**
 * A POST whose answer arrives in pieces (text/event-stream).
 *
 * EventSource cannot POST and cannot send an Authorization header, so this
 * reads the body itself. It lives here rather than in the assistant because
 * this is the file that knows about tokens, refreshing and the error contract —
 * and a stream needs all three exactly as much as a plain request does.
 *
 * No timeout: a conversation that consults three things legitimately takes
 * longer than thirty seconds. The caller passes an AbortSignal instead, which
 * is what a Stop button needs anyway.
 *
 * @param {string} path
 * @param {object|FormData} body  JSON, or a FormData when it carries a file
 *        (the assistant's call sends the recording and reads the turn as it
 *        happens)
 * @param {object} options
 * @param {Function} options.onEvent  called once per event, already parsed
 * @param {AbortSignal} [options.signal]
 */
async function streamRequest(path, body, { onEvent, signal } = {}, isRetry = false) {
  const token = getToken()

  let response
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      headers: {
        // The browser writes a FormData's own boundary; naming the type here
        // would break it.
        ...(body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body instanceof FormData ? body : JSON.stringify(body),
      signal,
    })
  } catch (error) {
    if (error.name === 'AbortError') throw new ApiError('Cancelled', 0, 'ABORTED')
    throw new ApiError('Network error', 0, 'NETWORK_ERROR')
  }

  if (response.status === 401 && !isRetry) {
    try {
      await ensureFreshToken()
      return streamRequest(path, body, { onEvent, signal }, true)
    } catch (error) {
      clearSession()
      sessionLost(error)
      throw new ApiError('Session expired', 401, 'SESSION_EXPIRED')
    }
  }

  // A failure before the stream opens still answers in the normal JSON shape.
  if (!response.ok) {
    let payload = null
    try { payload = await response.json() } catch { /* empty body */ }
    announce(payload?.code)
    throw new ApiError(
      payload?.message || `Error ${response.status}`,
      response.status,
      payload?.code || 'INTERNAL_ERROR',
      {
        details: payload?.details,
        fields: payload?.fields,
        requestId: payload?.requestId ?? response.headers.get('X-Request-Id'),
      }
    )
  }

  const reader = response.body.getReader()

  try {
    await readEvents(reader, onEvent)
  } finally {
    // Whether the stream ended, failed or the caller threw, the connection is
    // released rather than left hanging on the server.
    reader.cancel().catch(() => {})
  }
}

async function readEvents(reader, onEvent) {
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })

    // Events are separated by a blank line; the tail is an incomplete event
    // that has to wait for the next chunk.
    const parts = buffer.split('\n\n')
    buffer = parts.pop() ?? ''

    for (const part of parts) {
      const line = part.split('\n').find(entry => entry.startsWith('data:'))
      if (!line) continue

      let event
      try {
        event = JSON.parse(line.slice(5).trim())
      } catch {
        // A malformed event is dropped rather than killing the stream.
        continue
      }

      // Deliberately outside the try above: a handler that throws — which is
      // how the caller reports an error event — must reach the caller, not be
      // mistaken for a parse failure.
      onEvent?.(event)
    }
  }
}

/**
 * A request whose answer is a file, not JSON.
 *
 * Here for the same reason `streamRequest` is: this is the file that knows
 * about the base URL, the token, refreshing it, and the error contract, and a
 * download needs all four exactly as much as a plain request does. A module
 * reaching for bare `fetch()` gets none of them — it hardcodes a URL that is
 * only right when the SPA and the API share an origin, it fails after fifteen
 * minutes when the access token expires, and it throws an Error with no `code`,
 * which the interface cannot translate.
 *
 * A GET by default. `body` makes it a POST, which a document endpoint needs
 * when what it renders depends on more than an id — the translated headings of
 * a quote PDF, a report's column selection — and none of that belongs in a
 * query string.
 *
 * @returns {Promise<{blob: Blob, filename: string, sha256: string|null}>}
 *   `filename` comes from the server's Content-Disposition, so the name of a
 *   report is decided in one place. `sha256` is the seal an export may carry
 *   (X-Content-SHA256) to prove months later that a file is the one this system
 *   produced; null when the endpoint does not seal.
 */
async function download(path, { params, body, method, timeout } = {}, isRetry = false) {
  const token = getToken()
  const query = queryString(params)

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeout ?? TIMEOUT_MS)

  let response
  try {
    response = await fetch(`${BASE_URL}${path}${query}`, {
      method: method ?? (body === undefined ? 'GET' : 'POST'),
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      signal: controller.signal,
    })
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new ApiError('The download took too long. Try again.', 0, 'TIMEOUT')
    }
    throw new ApiError('Network error', 0, 'NETWORK_ERROR')
  } finally {
    clearTimeout(timer)
  }

  // Same 401 dance as request(): refresh once, replay once.
  if (response.status === 401 && !isRetry) {
    try {
      await ensureFreshToken()
      return download(path, { params, body, method, timeout }, true)
    } catch (error) {
      clearSession()
      sessionLost(error)
      throw new ApiError('Session expired', 401, 'SESSION_EXPIRED')
    }
  }

  if (!response.ok) {
    // A failed download still answers with the normal JSON envelope, so the
    // caller gets a translatable code rather than "Error 403".
    let payload = null
    try { payload = await response.json() } catch { /* not JSON */ }
    announce(payload?.code)
    throw new ApiError(
      payload?.message || `Error ${response.status}`,
      response.status,
      payload?.code || 'INTERNAL_ERROR',
      {
        details: payload?.details,
        fields: payload?.fields,
        requestId: payload?.requestId ?? response.headers.get('X-Request-Id'),
      }
    )
  }

  return {
    blob: await response.blob(),
    filename: filenameFrom(response.headers.get('Content-Disposition')) ?? 'download',
    sha256: response.headers.get('X-Content-SHA256'),
  }
}

/**
 * A POST whose body is a file and whose progress is worth watching.
 *
 * XMLHttpRequest, and the one place in this file that is not fetch. It is not
 * nostalgia: `fetch` cannot report UPLOAD progress — the request body is
 * streamed with no event to hook, and `ReadableStream` request bodies are
 * unsupported on the browsers this ERP has to run on. A 20 MB scan over a site
 * connection with no bar is a screen that looks frozen for a minute, and the
 * person cancels and tries again, which uploads it twice.
 *
 * Everything else is deliberately identical to `request()`: same base URL, same
 * bearer token, same single-flight refresh with one replay, same ApiError with
 * the backend's stable `code`. A module reaching for bare XHR gets none of
 * that.
 *
 * Content-Type is never set: the browser writes it itself, with the multipart
 * boundary, and setting it by hand produces a body the server cannot parse.
 *
 * @param {string} path
 * @param {FormData} form
 * @param {object} [options]
 * @param {(percent: number, loaded: number, total: number) => void} [options.onProgress]
 *        0-100. Called on `progress` events, and once with 100 when the body is
 *        away — the server is still working at that point, which is why the
 *        caller usually shows "processing" rather than "done" until it resolves.
 * @param {AbortSignal} [options.signal]  what a Cancel button aborts
 * @returns {Promise<any>} the `data` half of the envelope, like every other verb
 */
function upload(path, form, { onProgress, signal, timeout } = {}, isRetry = false) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new ApiError('Cancelled', 0, 'ABORTED'))
      return
    }

    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${BASE_URL}${path}`)
    // Uploads are legitimately slower than a request: the default 30 s ceiling
    // would kill a 25 MB file on any connection worth calling rural.
    xhr.timeout = timeout ?? 0

    const token = getToken()
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)

    const onAbort = () => xhr.abort()
    signal?.addEventListener('abort', onAbort)
    const done = () => signal?.removeEventListener('abort', onAbort)

    xhr.upload.addEventListener('progress', (event) => {
      if (!event.lengthComputable) return
      onProgress?.(Math.round((event.loaded / event.total) * 100), event.loaded, event.total)
    })
    xhr.upload.addEventListener('load', () => onProgress?.(100))

    xhr.addEventListener('abort', () => { done(); reject(new ApiError('Cancelled', 0, 'ABORTED')) })
    xhr.addEventListener('timeout', () => {
      done()
      reject(new ApiError('The upload took too long. Try again.', 0, 'TIMEOUT'))
    })
    xhr.addEventListener('error', () => { done(); reject(new ApiError('Network error', 0, 'NETWORK_ERROR')) })

    xhr.addEventListener('load', async () => {
      done()

      let payload = null
      try { payload = JSON.parse(xhr.responseText) } catch { /* empty or not JSON */ }

      // The same 401 dance as request(): refresh once, replay once. A FormData
      // can be sent again — it is not a consumed stream — so the replay is a
      // real retry and not a silently empty second upload.
      if (xhr.status === 401 && !isRetry && payload?.code !== 'SESSION_REVOKED') {
        try {
          await ensureFreshToken()
          resolve(await upload(path, form, { onProgress, signal, timeout }, true))
        } catch (error) {
          clearSession()
          sessionLost(error)
          reject(new ApiError('Session expired', 401, 'SESSION_EXPIRED'))
        }
        return
      }

      if (xhr.status < 200 || xhr.status >= 300) {
        announce(payload?.code)
        reject(new ApiError(
          payload?.message || `Error ${xhr.status}`,
          xhr.status,
          payload?.code || 'INTERNAL_ERROR',
          {
            details: payload?.details,
            fields: payload?.fields,
            requestId: payload?.requestId ?? xhr.getResponseHeader('X-Request-Id'),
          }
        ))
        return
      }

      resolve(payload?.data ?? payload)
    })

    xhr.send(form)
  })
}

/**
 * Reads the name out of a Content-Disposition header.
 * Handles both `filename="x.csv"` and the RFC 5987 `filename*=UTF-8''x.csv`
 * that platform/files sends, which is the one that survives accents.
 */
function filenameFrom(header) {
  if (!header) return null

  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (encoded) {
    try { return decodeURIComponent(encoded[1].trim()) } catch { /* fall through */ }
  }

  const plain = /filename="?([^";]+)"?/i.exec(header)
  return plain ? plain[1].trim() : null
}

/**
 * Hands a downloaded file to the browser.
 *
 * One line of DOM, but every module would otherwise write its own and one of
 * them would forget to revoke the object URL — which leaks the whole file until
 * the tab is closed.
 */
export function saveFile(blob, filename) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename || 'download'
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

export const api = {
  get:    (path, options) => request('GET', path, undefined, options),
  post:   (path, body, options) => request('POST', path, body, options),
  patch:  (path, body, options) => request('PATCH', path, body, options),
  put:    (path, body, options) => request('PUT', path, body, options),
  delete: (path, body, options) => request('DELETE', path, body, options),

  /** A POST that reports progress as it happens. See streamRequest above. */
  stream: (path, body, options) => streamRequest(path, body, options),

  /** A request that answers with a file. GET, or POST when given a body. */
  download: (path, options) => download(path, options),

  /**
   * A POST of a file that reports how far it has got. See upload() above for
   * why this one is not fetch.
   */
  upload: (path, form, options) => upload(path, form, options),

  /** Absolute URL, e.g. to open a stream or a file from an <img>. */
  url: (path) => `${BASE_URL}${path}`,

  /**
   * For media elements and downloads, which cannot send headers. The token in
   * the URL is a short media token (see mediaToken.js), not the access token,
   * once one has been fetched; the stream uses its own one-use ticket.
   */
  urlWithToken: (path) => `${BASE_URL}${path}?token=${encodeURIComponent(mediaTokens.forUrl(getToken()))}`,
}

export default api
